import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { products } from "../data/products";
import Button from "../components/ui/Button";
import { useCart } from "../context/CartContext";

export default function Product() {
  const { id } = useParams();
  const { addToCart } = useCart();

  const product = useMemo(() => products.find((p) => p.id === id), [id]);

  const [imgIndex, setImgIndex] = useState(0);
  const [qty, setQty] = useState(1);

  // ✅ NEW: variant state
  const [selectedVariant, setSelectedVariant] = useState(
    product?.variants?.[0] || null
  );

  // ✅ Tabs
  const [tab, setTab] = useState("description");

  if (!product) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <div className="text-xl font-semibold text-slate-900">
            Product not found
          </div>
          <Link
            to="/shop"
            className="mt-3 inline-block text-sm text-slate-700 hover:text-slate-900"
          >
            ← Back to shop
          </Link>
        </div>
      </div>
    );
  }

  const coaLink = product.coaUrl || "https://example.com/coa";

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <nav className="text-sm text-slate-600">
          <Link to="/" className="hover:text-slate-900">
            Home
          </Link>{" "}
          <span className="mx-2">/</span>
          <Link to="/shop" className="hover:text-slate-900">
            Shop
          </Link>{" "}
          <span className="mx-2">/</span>
          <span className="text-slate-900">{product.name}</span>
        </nav>

        <div className="mt-12 grid gap-8 lg:grid-cols-3 items-start">
          {/* IMAGE */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="bg-slate-50">
                <div className="mx-auto max-w-[600px]">
                  <div className="aspect-[4/5] sm:aspect-square">
                    <img
                      src={
                        selectedVariant?.image ||
                        product.images?.[imgIndex]
                      }
                      alt={product.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* BUY BOX */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm lg:sticky lg:top-24">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="mt-4 text-2xl font-semibold text-slate-900">
                    {product.name}
                  </h1>
                </div>

                <div className="text-right">
                  <div className="text-xs text-slate-600">Price</div>
                  <div className="text-2xl font-semibold text-slate-900">
                    $
                    {Number(
                      selectedVariant?.price || product.price
                    ).toFixed(2)}
                  </div>
                </div>
              </div>

              {/* ✅ DOSE SELECTOR */}
              {product.variants && (
                <div className="mt-6">
                  <div className="text-sm font-medium text-slate-900">
                    Dose
                  </div>

                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {product.variants.map((variant) => (
                      <button
                        key={variant.id}
                        onClick={() => setSelectedVariant(variant)}
                        className={[
                          "rounded-xl border px-4 py-2 text-sm font-semibold transition",
                          selectedVariant?.id === variant.id
                            ? "border-blue-700 bg-blue-50 text-blue-800"
                            : "border-slate-200 hover:bg-slate-50",
                        ].join(" ")}
                      >
                        {variant.label} — ${variant.price}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Assurance */}
              <ul className="mt-10 mb-10 space-y-3 text-sm text-slate-600">
                <li className="flex gap-3">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-blue-600 flex-shrink-0" />
                  <span>
                    High-purity material with clearly defined specifications and
                    documentation
                  </span>
                </li>

                <li className="flex gap-3">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-blue-600 flex-shrink-0" />
                  <span>
                    Presented in sealed packaging with transparent storage and
                    handling guidance
                  </span>
                </li>

                <li className="flex gap-3">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-blue-600 flex-shrink-0" />
                  <span>
                    Certificate of Analysis (CoA) or lab documentation provided
                    per product
                  </span>
                </li>
              </ul>

              <div className="my-5 h-px bg-slate-200" />

              {/* Quantity */}
              <div>
                <div className="text-sm font-medium text-slate-900">
                  Quantity
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <button
                    className="h-10 w-10 rounded-xl border border-slate-200 hover:bg-slate-50"
                    onClick={() =>
                      setQty((q) => Math.max(1, q - 1))
                    }
                  >
                    −
                  </button>

                  <div className="min-w-[54px] text-center text-sm font-semibold text-slate-900">
                    {qty}
                  </div>

                  <button
                    className="h-10 w-10 rounded-xl border border-slate-200 hover:bg-slate-50"
                    onClick={() => setQty((q) => q + 1)}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Buttons */}
              <div className="mt-6 grid gap-2">
                <Button
                  className="w-full py-3"
                  onClick={() =>
                    addToCart(
                      {
                        ...product,
                        variant: selectedVariant,
                        price:
                          selectedVariant?.price || product.price,
                        name: selectedVariant
                          ? `${product.name} (${selectedVariant.label})`
                          : product.name,
                        image:
                          selectedVariant?.image ||
                          product.images?.[0],
                      },
                      qty
                    )
                  }
                >
                  Add to cart
                </Button>

                <Link to="/cart" className="block">
                  <Button variant="secondary" className="w-full py-3">
                    View cart
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* TABS */}
        <div className="mt-20 mb-20 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-3 bg-slate-50 border-b border-slate-200">
            <TabButton
              active={tab === "description"}
              onClick={() => setTab("description")}
            >
              Description
            </TabButton>

            <TabButton
              active={tab === "specs"}
              onClick={() => setTab("specs")}
            >
              Specs
            </TabButton>

            <TabButton
              active={tab === "lab"}
              onClick={() => setTab("lab")}
            >
              Purity / Lab Results
            </TabButton>
          </div>

          <div className="p-6 h-[280px] overflow-y-auto">
            {tab === "description" && (
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-slate-900">
                  Product description
                </h3>

                <div className="text-sm leading-relaxed text-slate-700 space-y-2">
                  {product.description.split("\n").map((line, i) =>
                    line.startsWith("- ") ? (
                      <li key={i} className="ml-5 list-disc">
                        {line.replace("- ", "")}
                      </li>
                    ) : (
                      <p key={i}>{line}</p>
                    )
                  )}
                </div>
              </div>
            )}

            {tab === "specs" && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">
                  Specifications
                </h3>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <InfoCard
                    label="Category"
                    value={product.categories[0]}
                  />
                  <InfoCard label="Purity" value={product.purity} />
                  <InfoCard label="Format" value={product.format} />
                  <InfoCard label="Storage" value={product.storage} />
                </div>
              </div>
            )}

            {tab === "lab" && (
              <div className="space-y-5">
                <h3 className="text-lg font-semibold text-slate-900">
                  Purity & Laboratory Results
                </h3>

                <p className="text-sm text-slate-700">
                  Certificates of Analysis (CoA) are provided for transparency.
                </p>

                <a
                  href={coaLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-xl px-5 py-2.5 text-sm font-semibold bg-blue-700 text-white hover:bg-blue-800"
                >
                  View CoA
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={[
        "px-4 py-4 text-sm font-semibold",
        active
          ? "bg-white text-blue-800 border-b-2 border-blue-700"
          : "bg-slate-50 text-slate-600 hover:bg-slate-100",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-sm font-semibold text-slate-900">
        {value}
      </div>
    </div>
  );
}
